import moment from "moment";

import {
  Bank,
  BankCreditNoteRequest,
  BankCreditRequestChildResponse,
  BankCreditResponse,
  NoteType,
} from "types";

export const AUTOMATIC_BANK_CREDIT_NOTE_BANKS: Bank[] = ["BANK_NOW", "CEMBRA_MONEY_AG", "CORNER"];

type AutomaticBankCreditNote = BankCreditNoteRequest & {
  creationType: "AUTOMATIC";
  code: string;
};

type AutomaticBankCreditNoteRule = {
  code: string;
  banks: Bank[];
  noteType: NoteType;
  isActive: (credit: BankCreditResponse, bank: Bank) => boolean;
  getNote: (credit: BankCreditResponse, bank: Bank) => string;
};

const normalize = (value?: string) => (value ?? "").trim().toUpperCase();

const isOneOf = (value: string | undefined, expected: string[]) =>
  expected.includes(normalize(value));

const hasPositiveAmount = (value?: number) => typeof value === "number" && value > 0;

const formatAmount = (value?: number) => `${Math.round(value ?? 0).toLocaleString("de-CH")} CHF`;

const formatDate = (value?: Date) => (value ? moment(value).format("DD.MM.YYYY") : "");

const monthsSince = (value?: Date) => {
  if (!value || !moment(value).isValid()) {
    return undefined;
  }
  return moment().diff(moment(value), "months");
};

const yearsSince = (value?: Date) => {
  if (!value || !moment(value).isValid()) {
    return undefined;
  }
  return moment().diff(moment(value), "years");
};

const getMainEmployments = (credit: BankCreditResponse) =>
  credit.employments?.filter((employment) => !employment.partner) ?? [];

const getMainIncomes = (credit: BankCreditResponse) =>
  credit.incomes?.filter((income) => !income.partner) ?? [];

const getMainSalary = (credit: BankCreditResponse) =>
  getMainEmployments(credit).reduce((sum, employment) => sum + (employment.salary ?? 0), 0);

const getAlimonyIncomeAmount = (credit: BankCreditResponse) =>
  getMainIncomes(credit)
    .filter((income) => income.incomeType === "ALIMONY")
    .reduce((sum, income) => sum + (income.amount ?? 0), 0);

const getRentIncomeAmount = (credit: BankCreditResponse) =>
  getMainIncomes(credit)
    .filter((income) => income.incomeType === "RENT")
    .reduce((sum, income) => sum + (income.amount ?? 0), 0);

const getMonthlyAlimonyExpenseByBank = (credit: BankCreditResponse, bank: Bank) => {
  if (bank === "BANK_NOW") {
    return credit.monthlyAlimonyExpensesBn;
  }
  if (bank === "CEMBRA_MONEY_AG") {
    return credit.monthlyAlimonyExpensesCb;
  }
  if (bank === "CORNER") {
    return credit.monthlyAlimonyExpensesCc;
  }
  return undefined;
};

const getCembraCalculatedAlimonyMax = (credit: BankCreditResponse) =>
  (credit.children ?? []).reduce((sum, child) => {
    const age = yearsSince(child.birthdate);
    if (age === undefined) {
      return sum;
    }
    return sum + (age < 10 ? 400 : 600);
  }, 0);

const childrenCaretakerLabels: Record<string, string> = {
  PARENTS: "die Eltern",
  GRANDMOTHER: "die Grossmutter",
  GRANDFATHER: "der Grossvater",
  BROTHER: "der Bruder",
  SISTER: "die Schwester",
  THIRD_PERSON: "eine Drittperson",
};

const getSharedHousingPeople = (credit: BankCreditResponse) =>
  (credit.housingExpenses ?? [])
    .map((housingExpense) => housingExpense.costSharingPerson?.trim())
    .filter((person): person is string => Boolean(person));

const isChildLivingWithClient = (credit: BankCreditResponse, child: BankCreditRequestChildResponse) =>
  credit.childrenLiveWithYou === "YES" ||
  (credit.childrenLiveWithYou === "PARTIALLY" && child.address === "WITH_CLIENT");

const getSharedAdultChildren = (credit: BankCreditResponse) => {
  const sharedPeople = new Set(getSharedHousingPeople(credit).map(normalize));

  return (credit.children ?? []).filter((child) => {
    const age = yearsSince(child.birthdate);
    const fullName = [child.firstName, child.lastName].filter(Boolean).join(" ");

    return (
      age !== undefined &&
      age >= 18 &&
      isChildLivingWithClient(credit, child) &&
      Boolean(fullName) &&
      sharedPeople.has(normalize(fullName))
    );
  });
};

const getWorkingSharedAdultChildren = (credit: BankCreditResponse) =>
  getSharedAdultChildren(credit).filter((child) => hasPositiveAmount(child.craftAmount));

const hasSelfEmployedMainEmployment = (credit: BankCreditResponse) =>
  getMainEmployments(credit).some((employment) =>
    ["SELF_EMPLOYED_LUMP_SUM", "SELF_EMPLOYED_AG"].includes(employment.professionalSituation),
  );

const hasAgencyEmploymentShorterThanOneYear = (credit: BankCreditResponse) =>
  getMainEmployments(credit).some((employment) => {
    if (employment.professionalSituation !== "AGENCY") {
      return false;
    }
    const months = monthsSince(employment.contractStartDate);
    return months !== undefined && months < 12;
  });

const hasMainProfessionalSituation = (
  credit: BankCreditResponse,
  professionalSituation: "AGENCY" | "PENSION" | "DISABILITY_PENSION",
) => getMainEmployments(credit).some((employment) => employment.professionalSituation === professionalSituation);

const hasSuvaAdditionalIncome = (credit: BankCreditResponse) =>
  getMainIncomes(credit).some((income) => income.incomeType === "PENSIONSKASSE_SUVA");

const getMaximumRepaymentMonths = (credit: BankCreditResponse, bank: Bank) => {
  if (bank === "BANK_NOW" && hasMainProfessionalSituation(credit, "AGENCY")) {
    return 36;
  }

  if (hasMainProfessionalSituation(credit, "DISABILITY_PENSION")) {
    return hasSuvaAdditionalIncome(credit) ? 48 : 36;
  }

  const pensionAge = hasMainProfessionalSituation(credit, "PENSION") ? 70 : 65;
  if (!credit.birthDate) {
    return undefined;
  }

  return moment(credit.birthDate).add(pensionAge, "years").diff(moment(), "months");
};

const hasHourlySalaryStructure = (credit: BankCreditResponse) =>
  getMainEmployments(credit).some((employment) => employment.salaryStructure === "HOURLY");

const hasThirteenSalaries = (credit: BankCreditResponse) =>
  getMainEmployments(credit).some((employment) => employment.numOfSalariesPerYear === 13);

const hasBonusType = (credit: BankCreditResponse, bonusType: "ANNUAL" | "MONTHLY" | "QUARTERLY") =>
  getMainEmployments(credit).some(
    (employment) => employment.bonusReceived && employment.bonusType === bonusType,
  );

const hasSickLeaveOnPaylist = (credit: BankCreditResponse) =>
  getMainEmployments(credit).some((employment) => employment.sickLeaveOnPaylist);

const getSickLeaveReturnDate = (credit: BankCreditResponse) =>
  getMainEmployments(credit).find(
    (employment) =>
      employment.sickLeaveOnPaylist &&
      employment.sickLeaveReturnDate &&
      moment(employment.sickLeaveReturnDate).isValid(),
  )?.sickLeaveReturnDate;

const hasChildAllowanceMismatch = (credit: BankCreditResponse) =>
  getMainEmployments(credit).some((employment) => employment.childAllowanceMatches === false);

const hasEmployerLoan = (credit: BankCreditResponse) =>
  getMainEmployments(credit).some((employment) => hasPositiveAmount(employment.employerLoanAmount));

const getEmployerLoanEmployment = (credit: BankCreditResponse) =>
  getMainEmployments(credit).find((employment) => hasPositiveAmount(employment.employerLoanAmount));

const hasInvisibleIbanOnPaylist = (credit: BankCreditResponse) =>
  getMainEmployments(credit).some((employment) => employment.ibanOnPaylist === false);

const hasLongTravelDistance = (credit: BankCreditResponse) =>
  getMainEmployments(credit).some((employment) => (employment.travelingDistance ?? 0) > 10);

const neighboringNationalities = [
  "ITALY",
  "ITALIEN",
  "ITALIA",
  "AUSTRIA",
  "OESTERREICH",
  "ÖSTERREICH",
  "GERMANY",
  "DEUTSCHLAND",
  "FRANCE",
  "FRANKREICH",
  "LIECHTENSTEIN",
];

const refugeeStatusNationalities = ["IRAN", "IRAK", "AFGHANISTAN"];

const rules: AutomaticBankCreditNoteRule[] = [
  {
    code: "creditAmount.bankNow.minimum",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: (credit) => hasPositiveAmount(credit.creditAmount) && credit.creditAmount! < 10000,
    getNote: () => "Mind. Betrag sind 10'000CHF.",
  },
  {
    code: "repaymentPeriod.maximum",
    banks: ["BANK_NOW", "CEMBRA_MONEY_AG", "CORNER"],
    noteType: "BANK",
    isActive: (credit, bank) => {
      const maximum = getMaximumRepaymentMonths(credit, bank);
      return maximum !== undefined && maximum > 0 && (credit.paymentPeriod ?? 0) >= maximum;
    },
    getNote: (credit, bank) =>
      `MAX Laufzeit ${getMaximumRepaymentMonths(credit, bank)} Monate.`,
  },
  {
    code: "creditType.separate",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "BANK",
    isActive: (credit) => credit.creditType === "SEPARATE",
    getNote: () => "Kunde wünscht separaten Kredit.",
  },
  {
    code: "creditPurpose.over80000",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) => (credit.creditAmount ?? 0) > 80000,
    getNote: () =>
      "Der gewünschte Betrag liegt über 80.000 CHF. Bitte prüfen Sie den Verwendungszweck des Darlehens.",
  },
  {
    code: "productBankNow.casaOwnershipTooShort",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: (credit) => {
      const livingMonths = monthsSince(credit.livingFrom);
      return (
        credit.mortgage === true &&
        credit.productBn === "CLASSIC" &&
        livingMonths !== undefined &&
        livingMonths <= 6
      );
    },
    getNote: () => "Kunde ist nicht Eigentümer mehr als 6 Monate, daher kein Casa Kredit.",
  },
  {
    code: "housing.bankNow.rentDetails",
    banks: ["BANK_NOW"],
    noteType: "BANK",
    isActive: (credit) => credit.rent === true,
    getNote: () =>
      "Alle Budgetpositionen gemäss Vorgaben abgefragt und gemäss Kundenangabe erfasst / Aktuelle Wohnkosten.",
  },
  {
    code: "housing.bankNow.mortgageDetails",
    banks: ["BANK_NOW"],
    noteType: "BANK",
    isActive: (credit) => {
      const livingMonths = monthsSince(credit.livingFrom);
      return credit.mortgage === true && livingMonths !== undefined && livingMonths > 6;
    },
    getNote: () =>
      "Alle Budgetpositionen gemäss Vorgaben abgefragt und gemäss Kundenangabe erfasst / Gesamter Hypothekarbetrag.",
  },
  {
    code: "refinanceLease.selected",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) => credit.refinanceLease === true,
    getNote: () => "Kunde möchte Leasing ablösen.",
  },
  {
    code: "fullName.cembra.tooLong",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "BANK",
    isActive: (credit) => (credit.user?.fullName?.trim().length ?? 0) > 29,
    getNote: (credit) => `Voller Name des Kundens: ${credit.user.fullName}`,
  },
  {
    code: "nationality.neighboringCountry.borderWorkerCheck",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) => isOneOf(credit.nationality, neighboringNationalities),
    getNote: () => "Prüfen ob der Kunde Grenzgänger war.",
  },
  {
    code: "nationality.romania.bVisa.bankNow",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      isOneOf(credit.nationality, ["ROMANIA", "RUMÄNIEN", "RUMAENIEN"]) && credit.visaType === "B",
    getNote: () => "Rumänen mit B Bewilligung können nicht an die Bank Now gesendet werden.",
  },
  {
    code: "nationality.bulgaria.bVisa.cembra",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) =>
      isOneOf(credit.nationality, ["BULGARIA", "BULGARIEN"]) && credit.visaType === "B",
    getNote: () => "Bulgaren mit B Bewilligung können nicht an die Cembra gesendet werden.",
  },
  {
    code: "nationality.refugeeStatus.check",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) => isOneOf(credit.nationality, refugeeStatusNationalities),
    getNote: () => "Prüfen ob der Kunde einen Flüchtlingsstatus hat.",
  },
  {
    code: "children.abroad",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "BANK",
    isActive: (credit) => credit.childrenInAbroad === true,
    getNote: () => "Kind/er im Ausland.",
  },
  {
    code: "children.foreignTransferAmount",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "BANK",
    isActive: (credit) =>
      credit.childrenInAbroad === true && hasPositiveAmount(credit.foreignTransferAmount),
    getNote: (credit) => `Kunde sendet ins Ausland ${formatAmount(credit.foreignTransferAmount)}.`,
  },
  {
    code: "children.foreignTransferAmount.cembraMinimum",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "BANK",
    isActive: (credit) =>
      credit.childrenInAbroad === true && hasPositiveAmount(credit.foreignTransferAmount),
    getNote: () => "Cembra: Mindestbetrag für Auslagen im Ausland sind die Kinderzulagen.",
  },
  {
    code: "income.alimony",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "BANK",
    isActive: (credit) => hasPositiveAmount(getAlimonyIncomeAmount(credit)),
    getNote: (credit) => {
      const customerLabel = credit.gender === "FEMALE" ? "Kundin" : "Kunde";
      return `${customerLabel} bekommt ${formatAmount(getAlimonyIncomeAmount(credit))} Alimente.`;
    },
  },
  {
    code: "alimony.expense",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "BANK",
    isActive: (credit, bank) =>
      credit.paysAlimony === true &&
      hasPositiveAmount(getMonthlyAlimonyExpenseByBank(credit, bank)),
    getNote: (credit, bank) =>
      `Kunde zahlt Alimente ${formatAmount(getMonthlyAlimonyExpenseByBank(credit, bank))}.`,
  },
  {
    code: "children.craft.cembra.rentReduction",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) => (credit.children ?? []).some((child) => child.craft),
    getNote: () => "Cembra: Miete wurde -250CHF reduziert wegen Lehrlingslohn.",
  },
  {
    code: "alimony.cembra.maxCalculated",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "BANK",
    isActive: (credit) => {
      const calculatedMax = getCembraCalculatedAlimonyMax(credit);
      return calculatedMax > 0 && (credit.monthlyAlimonyExpensesCb ?? 0) > calculatedMax;
    },
    getNote: (credit) =>
      `Cembra: Alimente werden max ${formatAmount(getCembraCalculatedAlimonyMax(credit))} berechnet.`,
  },
  {
    code: "alimony.duration.check",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) => credit.paysAlimony === true && (credit.children?.length ?? 0) > 0,
    getNote: () => "Alimentendauer beachten! Alter des Kindes prüfen.",
  },
  {
    code: "children.caretaker.cembra",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "BANK",
    isActive: (credit) => !!credit.childrenCaretaker,
    getNote: (credit) => {
      const caretaker = childrenCaretakerLabels[credit.childrenCaretaker ?? ""] ?? "eine Person";
      return `[CB] ${caretaker} passt auf das Kind auf.`;
    },
  },
  {
    code: "partner.abroad",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) => credit.partnerResidence === "ABROAD",
    getNote: () => "Ehepartner lebt im Ausland.",
  },
  {
    code: "professional.selfEmployedBVisa.cembra",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) => credit.visaType === "B" && hasSelfEmployedMainEmployment(credit),
    getNote: () => "Selbständig mit B Bewilligung kann nicht in die CB eingereicht werden.",
  },
  {
    code: "professional.agencyShortBankNow",
    banks: ["BANK_NOW"],
    noteType: "PROCESSING",
    isActive: hasAgencyEmploymentShorterThanOneYear,
    getNote: () => "Temporär kürzer als 1 Jahr kann nicht an die Bank Now gesendet werden.",
  },
  {
    code: "salaryStructure.hourly",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: hasHourlySalaryStructure,
    getNote: () => "Kunde ist auf Std.Lohn muss 3 Lohnabrechnungen haben.",
  },
  {
    code: "income.net.cornerBelow3500",
    banks: ["CORNER"],
    noteType: "PROCESSING",
    isActive: (credit) => {
      const salary = getMainSalary(credit);
      return salary > 0 && salary < 3500;
    },
    getNote: () =>
      "Kunde kann wegen seinem Einkommen <3500CHF nicht bei Corner eingereicht werden.",
  },
  {
    code: "salary.thirteen",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: hasThirteenSalaries,
    getNote: () => "Lohnabrechnung 13ten Lohn einverlangen. (NOV-FEB)",
  },
  {
    code: "bonus.annual.cembra",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) => hasBonusType(credit, "ANNUAL"),
    getNote: () =>
      "Cembra: Jahresbonus, letzte 3 LAs mit Jahresbonus erforderlich. (der kleinste wird berechnet)",
  },
  {
    code: "bonus.quarterly.cembra",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) => hasBonusType(credit, "QUARTERLY"),
    getNote: () =>
      "Cembra: Quartalbonus letzte 3 LAs mit Quartalbonus erforderlich. (durchschnitt wird gerechnet)",
  },
  {
    code: "bonus.monthly.cembra",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) => hasBonusType(credit, "MONTHLY"),
    getNote: () =>
      "Cembra: Monatsbonus letzte 3 LAs mit Monatsbonus erforderlich. (durchschnitt wird gerechnet)",
  },
  {
    code: "sickLeave.confirmation",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: hasSickLeaveOnPaylist,
    getNote: () => "Bestätigung oder neue LA einverlangen.",
  },
  {
    code: "sickLeave.returnToWork",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "BANK",
    isActive: (credit) => getSickLeaveReturnDate(credit) !== undefined,
    getNote: (credit) =>
      `Kunde ist wieder 100% arbeitstätig ab ${formatDate(getSickLeaveReturnDate(credit))}.`,
  },
  {
    code: "childAllowance.mismatch",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: hasChildAllowanceMismatch,
    getNote: () => "Kinderzulagen abklären!",
  },
  {
    code: "employerLoan.advance",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "BANK",
    isActive: hasEmployerLoan,
    getNote: (credit) => {
      const employment = getEmployerLoanEmployment(credit);
      const dateText = employment?.employerLoanPaidOut
        ? ` bis ${formatDate(employment.employerLoanPaidOut)}`
        : "";
      return `Kunde hat einen Vorschuss beim Arbeitgeber${dateText} monatlich zahlt er ${formatAmount(employment?.employerLoanAmount)} ab.`;
    },
  },
  {
    code: "distance.workplaceCheck",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: hasLongTravelDistance,
    getNote: () => "Überprüfe den Arbeitsort.",
  },
  {
    code: "transport.bankNow.details",
    banks: ["BANK_NOW"],
    noteType: "BANK",
    isActive: (credit) =>
      getMainEmployments(credit).some(
        (employment) =>
          employment.travelingToWorkBn !== undefined || hasPositiveAmount(employment.travelExpensesBn),
      ),
    getNote: () =>
      "Alle Budgetpositionen gemäss Vorgaben abgefragt und gemäss Kundenangabe erfasst / Transportmittel / Aktuelle Wohnkosten.",
  },
  {
    code: "iban.notVisible",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: hasInvisibleIbanOnPaylist,
    getNote: () =>
      "IBAN nicht ersichtlich. Bankkarte einverlangen. Prüfen ob Kunde den Lohn aus Konto bekommt.",
  },
  {
    code: "income.rent.cembra",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "PROCESSING",
    isActive: (credit) => hasPositiveAmount(getRentIncomeAmount(credit)),
    getNote: (credit) =>
      `Cembra: Liegenschaftseinkommen werden ${formatAmount(getRentIncomeAmount(credit) * 0.6)} berechnet.`,
  },
  {
    code: "housing.additionalExpensesNotIncluded",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) => credit.additionalHousingExpensesIncluded === false,
    getNote: () => "Nebenkostenabrechnung einverlangen.",
  },
  {
    code: "housing.rentSharedWithAdultChildren",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "BANK",
    isActive: (credit) => getSharedAdultChildren(credit).length > 0,
    getNote: () => "Miete wurde geteilt mit erwachsenem/erwachsenen Kind/Kindern.",
  },
  {
    code: "housing.cembra.rentSharedWithWorkingAdultChildren",
    banks: ["CEMBRA_MONEY_AG"],
    noteType: "BANK",
    isActive: (credit) => getWorkingSharedAdultChildren(credit).length > 0,
    getNote: (credit) =>
      getWorkingSharedAdultChildren(credit).length === 1
        ? "„[CB] Miete wurde geteilt mit erwachsenem Kind.“"
        : "„[CB] Miete wurde geteilt mit erwachsenen Kindern.“",
  },
  {
    code: "enforcedCollection.check",
    banks: AUTOMATIC_BANK_CREDIT_NOTE_BANKS,
    noteType: "PROCESSING",
    isActive: (credit) => credit.enforcedCollection === true,
    getNote: () => "Betreibung einverlangen und CRIF prüfen.",
  },
];

// These codes are no longer desired, but remain managed so existing automatic notes are deleted.
const deprecatedAutomaticBankCreditNoteCodes = [
  "housing.bankNow.rentSharedWith",
  "housing.cembra.rentSharedWithChildren",
];

export const managedAutomaticBankCreditNoteCodes = new Set([
  ...rules.map((rule) => rule.code),
  ...deprecatedAutomaticBankCreditNoteCodes,
]);

export const buildAutomaticBankCreditNotes = (
  credit: BankCreditResponse,
): AutomaticBankCreditNote[] =>
  rules.flatMap((rule) =>
    rule.banks
      .filter((bank) => rule.isActive(credit, bank))
      .map((bank) => ({
        bankCreditRequestId: credit.id,
        bank,
        noteType: rule.noteType,
        creationType: "AUTOMATIC",
        code: rule.code,
        note: rule.getNote(credit, bank),
      })),
  );
